import { apiDelete, apiGet, apiPost, apiPut, type RequestOptions } from './index'

export type AutoResponderMatchOperator = 'starts_with' | 'contains' | 'equals'

export interface AutoResponderTrigger {
  id: number
  match_operator: AutoResponderMatchOperator
  trigger_text: string
  trigger_text_norm?: string
}

export interface AutoResponderConflictWarning {
  type: 'identical_trigger' | 'operator_overlap'
  other_rule_id: number
  message: string
}

export interface AutoResponderRule {
  id: number
  station_id: number
  name: string
  response_text: string
  enabled: boolean
  active_from: string | null
  active_until: string | null
  sort_order: number
  created_at: string | null
  created_by: number | null
  updated_at: string | null
  updated_by: number | null
  triggers: AutoResponderTrigger[]
  last_fired_at: string | null
  conflict_warnings?: AutoResponderConflictWarning[]
}

export interface AutoResponderPagination {
  total: number
  per_page: number
  current_page: number
  total_pages: number
}

export interface AutoResponderListResponse {
  rules: AutoResponderRule[]
  pagination: AutoResponderPagination
}

export interface AutoResponderRuleInput {
  id?: number
  name: string
  response_text: string
  enabled: boolean
  active_from: string | null
  active_until: string | null
  triggers: AutoResponderTriggerInput[]
  updated_at?: string | null
}

export interface AutoResponderTriggerInput {
  id?: number
  match_operator: AutoResponderMatchOperator
  trigger_text: string
}

export interface AutoResponderMutationResponse {
  rule: AutoResponderRule | null
  warnings: AutoResponderConflictWarning[]
}

export interface AutoResponderDeleteResponse {
  deleted: boolean
}

export interface AutoResponderSortResponse {
  sorted: boolean
}

export interface AutoResponderTestMatch {
  rule_id: number
  rule_name: string
  trigger_text: string
  trigger_operator: AutoResponderMatchOperator
  response_text: string
  source: 'draft' | 'existing'
}

export interface AutoResponderTestResponse {
  normalised_text: string
  match: AutoResponderTestMatch | null
  warnings: AutoResponderConflictWarning[]
}

export interface ListAutoResponderRulesParams {
  stationId: number
  page?: number
  perPage?: number
  search?: string
  enabled?: 'all' | 'enabled' | 'disabled'
  options?: RequestOptions
}

export async function listAutoResponderRules({
  stationId,
  page = 1,
  perPage = 20,
  search,
  enabled = 'all',
  options,
}: ListAutoResponderRulesParams): Promise<AutoResponderListResponse> {
  const params = new URLSearchParams()
  params.set('station_id', String(stationId))
  params.set('page', String(page))
  params.set('per_page', String(perPage))

  if (search && search.trim().length > 0) {
    params.set('search', search.trim())
  }

  if (enabled === 'enabled') {
    params.set('enabled', '1')
  } else if (enabled === 'disabled') {
    params.set('enabled', '0')
  }

  return apiGet<AutoResponderListResponse>(
    `auto/rules?${params.toString()}`,
    options,
  )
}

export async function createAutoResponderRule(
  stationId: number,
  payload: AutoResponderRuleInput,
  options?: RequestOptions,
): Promise<AutoResponderMutationResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  return apiPost<AutoResponderMutationResponse>(
    `auto/rules?${params.toString()}`,
    payload,
    options,
  )
}

export async function updateAutoResponderRule(
  stationId: number,
  ruleId: number,
  payload: AutoResponderRuleInput,
  options?: RequestOptions,
): Promise<AutoResponderMutationResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  return apiPut<AutoResponderMutationResponse>(
    `auto/rules/${ruleId}?${params.toString()}`,
    payload,
    options,
  )
}

export async function deleteAutoResponderRule(
  stationId: number,
  ruleId: number,
  options?: RequestOptions,
): Promise<AutoResponderDeleteResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  return apiDelete<AutoResponderDeleteResponse>(
    `auto/rules/${ruleId}?${params.toString()}`,
    options,
  )
}

export async function sortAutoResponderRules(
  stationId: number,
  orderedIds: number[],
  options?: RequestOptions,
): Promise<AutoResponderSortResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  return apiPost<AutoResponderSortResponse>(
    `auto/rules/sort?${params.toString()}`,
    { ordered_ids: orderedIds },
    options,
  )
}

export interface AutoResponderTestPayload {
  message_text: string
  draft_rule?: AutoResponderRuleInput
}

export async function testAutoResponderMessage(
  stationId: number,
  payload: AutoResponderTestPayload,
  options?: RequestOptions,
): Promise<AutoResponderTestResponse> {
  const params = new URLSearchParams({ station_id: String(stationId) })

  return apiPost<AutoResponderTestResponse>(
    `auto/rules/test?${params.toString()}`,
    payload,
    options,
  )
}
